import { DestroyRef, inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { AuthService } from '@core/auth';
import { ToastService } from '@shared/services';
import { TranslateService } from '@ngx-translate/core';
import { BanStatus, CoreData } from '@shared/models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FeatureFlagService } from './feature-flag.service';
import { AmplitudeEventService } from './amplitude-event.service';
import { ProviderSettingsService } from './provider-settings.service';

export type WarningData = {
  paymentMissing: boolean;
  customerAddressMissing: boolean;
  banStatus?: BanStatus;
};

@Injectable({
  providedIn: 'root',
})
export class CoreDataService {
  destroyRef = inject(DestroyRef);
  defaultCoreData: CoreData = {
    signup_status: {
      has_payment_method: false,
      has_customer_address: false,
      has_enabled_pay_per_use_license_plates: false,
    },
    customer_settings: {
      has_external_payment_method: false,
    },
    approval_status: {},
  };
  coreData = new BehaviorSubject<CoreData>(this.defaultCoreData);
  warningData = new Subject<WarningData>();

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private featureFlagService: FeatureFlagService,
    private amplitudeEventService: AmplitudeEventService,
    private providerSettingsService: ProviderSettingsService,
  ) {
    this.coreData.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (coreData) => {
        if (coreData.customer_id) {
          const properties: { hasPpuEnabled: boolean; hasInvoiceAddress: boolean; providerName?: string } = {
            hasPpuEnabled: coreData.signup_status.has_enabled_pay_per_use_license_plates,
            hasInvoiceAddress: coreData.signup_status.has_customer_address,
          };
          if (this.providerSettingsService.providerSettings.value.general_data.name) {
            properties.providerName = this.providerSettingsService.providerSettings.value.general_data.name;
          }

          this.amplitudeEventService.updateUserProperties({
            userId: coreData.customer_id,
            properties,
          });
        }
        this.warningData.next({
          paymentMissing: !coreData.signup_status.has_payment_method && !this.isPaymentMethodOptional(coreData),
          customerAddressMissing: !coreData.signup_status.has_customer_address,
          banStatus: coreData.ban_status,
        });
      },
    });
  }

  fetchCoreData(): Observable<CoreData> {
    return this.http.get<CoreData>(`/api/backoffice/v2/customer_portal/core/`).pipe(
      catchError((error) => {
        if (error.status === 401 || error.status === 403) {
          this.toastService.add({
            type: 'error',
            message: this.translateService.instant('Common.Errors.TokenInvalid'),
          });
          this.authService.logOut();
          return of();
        } else {
          return of(this.defaultCoreData);
        }
      }),
    );
  }

  // Determines if a payment method is optional for the current user based on external payment method and feature flags.
  isPaymentMethodOptional(coreData: CoreData = this.coreData.value): boolean {
    return coreData.customer_settings?.has_external_payment_method ||
      this.featureFlagService.getFeatureFlag('dev_payment_method_optional') === true ||
      this.featureFlagService.getFeatureFlag('enable_external_billing') === true;
  }

  setCoreData(coreData: CoreData): void {
    this.coreData.next(coreData);
  }

  setPaymentMethodAvailability(hasPaymentMethod: boolean, hideBanWarning: boolean = false): void {
    const currentData = this.coreData.value;
    if (hasPaymentMethod && hideBanWarning) {
      delete currentData.ban_status;
    }
    const updatedData = {
      ...currentData,
      signup_status: {
        ...currentData.signup_status,
        has_payment_method: hasPaymentMethod,
      },
    };
    this.coreData.next(updatedData);
  }

  setCustomerAddressCompletion(hasCustomerAddress: boolean): void {
    const currentData = this.coreData.value;
    const updatedData = {
      ...currentData,
      signup_status: {
        ...currentData.signup_status,
        has_customer_address: hasCustomerAddress,
      },
    };
    this.coreData.next(updatedData);
  }

  setEnabledPayPerUseLicensePlateStatus(hasEnabledPayPerUseLicensePlate: boolean): void {
    const currentData = this.coreData.value;
    const updatedData = {
      ...currentData,
      signup_status: {
        ...currentData.signup_status,
        has_enabled_pay_per_use_license_plates: hasEnabledPayPerUseLicensePlate,
      },
    };
    this.coreData.next(updatedData);
  }
}
